import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, ToggleButtonGroup, ToggleButton,
  CircularProgress, Alert, Paper, Stack,
} from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { vortxColors, PowerGraph } from '@vortx/shared-ui';
const PHD_API_BASE = process.env.REACT_APP_PHD_API_URL || 'http://localhost:5002';
import ResultsSummary from './ResultsSummary.jsx';

const LAYER_OPTIONS = [
  { value: 'unmanaged', label: 'Unmanaged' },
  { value: 'default', label: 'Default Flux' },
  { value: 'site_tuned', label: 'Site-Tuned' },
  { value: 'tuned_prediction', label: 'Site-Tuned + Prediction' },
];

/**
 * Pick a random weekday start hour between 336 (day 14) and 8088 (8760-672).
 * Prefers weekday starts (Mon-Fri), retries up to 10 times to find one.
 */
function pickRandomWindowStart() {
  const MIN_HOUR = 336;
  const MAX_HOUR = 8088;
  for (let attempt = 0; attempt < 10; attempt++) {
    const hour = MIN_HOUR + Math.floor(Math.random() * (MAX_HOUR - MIN_HOUR));
    // Jan 1 = Monday (index 0). dayOfWeek 0-4 = Mon-Fri.
    const dayOfYear = Math.floor(hour / 24);
    const dayOfWeek = dayOfYear % 7;
    if (dayOfWeek < 5) return hour;
  }
  // Fallback — return any valid hour
  return MIN_HOUR + Math.floor(Math.random() * (MAX_HOUR - MIN_HOUR));
}

/**
 * PredictionTestStep — runs 4-layer simulation and shows comparison.
 *
 * Props:
 *   scenario_data  — full scenario payload
 *   tier1_config   — Tier 1 analytical config from MetaTuningStep
 *   tier2_config   — Tier 2 calibration config from MetaTuningStep
 *   onBack()       — navigate to previous wizard step
 */
export default function PredictionTestStep({ scenario_data, tier1_config, tier2_config, onBack }) {
  const [windowStart, setWindowStart] = useState(() => pickRandomWindowStart());
  const [jobId, setJobId] = useState(null);
  const [results, setResults] = useState(null);
  const [layersComplete, setLayersComplete] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLayer, setSelectedLayer] = useState('tuned_prediction');

  const startSimulation = useCallback(async (startHour) => {
    setLoading(true);
    setError(null);
    setResults(null);
    setJobId(null);
    setLayersComplete(0);

    try {
      const response = await fetch(`${PHD_API_BASE}/api/calibration/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scenario_data,
          tier1_config,
          tier2_config,
          window_start_hour: startHour,
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || `Simulation request failed (${response.status})`);
      }

      const data = await response.json();
      // Synchronous response — results come back directly
      if (data.result) {
        setResults(data.result);
        setLayersComplete(4);
        setLoading(false);
      } else if (data.job_id) {
        setJobId(data.job_id);
      } else {
        throw new Error('Unexpected response from simulation endpoint');
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [scenario_data, tier1_config, tier2_config]);

  // Start simulation on mount and when windowStart changes
  useEffect(() => {
    startSimulation(windowStart);
  }, [windowStart, startSimulation]);

  // Poll for job completion
  useEffect(() => {
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(
          `${PHD_API_BASE}/api/calibration/simulate/status?job_id=${jobId}`
        );
        const data = await res.json();

        if (data.layers_complete != null) {
          setLayersComplete(data.layers_complete);
        }

        if (data.status === 'complete') {
          clearInterval(interval);
          setResults(data.result);
          setLoading(false);
        } else if (data.status === 'error') {
          clearInterval(interval);
          setError(data.error || 'Simulation failed');
          setLoading(false);
        }
      } catch (err) {
        clearInterval(interval);
        setError(`Polling failed: ${err.message}`);
        setLoading(false);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [jobId]);

  const handleReroll = () => {
    const newStart = pickRandomWindowStart();
    setWindowStart(newStart);
  };

  const handleLayerChange = (_event, newLayer) => {
    if (newLayer) setSelectedLayer(newLayer);
  };

  const graphData = results?.[selectedLayer]?.graph_data;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: vortxColors.white }}>
        Prediction Test
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: vortxColors.stone }}>
        Compare optimization layers on a random window starting at hour {windowStart}.
      </Typography>

      {loading && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <CircularProgress size={48} sx={{ color: vortxColors.gold, mb: 2 }} />
          <Typography variant="body1" sx={{ color: vortxColors.white }}>
            Running simulation... Layer {layersComplete}/4 complete...
          </Typography>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {results && (
        <>
          <ToggleButtonGroup
            value={selectedLayer}
            exclusive
            onChange={handleLayerChange}
            size="small"
            sx={{ mb: 2, flexWrap: 'wrap' }}
          >
            {LAYER_OPTIONS.map(opt => (
              <ToggleButton
                key={opt.value}
                value={opt.value}
                sx={{
                  textTransform: 'none',
                  color: vortxColors.stone,
                  borderColor: vortxColors.stone + '40',
                  '&.Mui-selected': {
                    bgcolor: vortxColors.gold,
                    color: vortxColors.charcoal,
                    fontWeight: 600,
                    '&:hover': { bgcolor: vortxColors.goldHover },
                  },
                  '&:hover': { borderColor: vortxColors.gold, color: vortxColors.white },
                }}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {graphData && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <PowerGraph data={graphData} isDark={true} />
            </Paper>
          )}

          <ResultsSummary results={results} />
        </>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            borderColor: vortxColors.stone + '60',
            color: vortxColors.stone,
            '&:hover': { borderColor: vortxColors.gold, color: vortxColors.white },
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          startIcon={<CasinoIcon />}
          onClick={handleReroll}
          disabled={loading}
          sx={{
            bgcolor: vortxColors.gold,
            '&:hover': { bgcolor: vortxColors.goldHover },
          }}
        >
          Re-roll Window
        </Button>
      </Stack>
    </Box>
  );
}
